import { useState } from "react";
import { FlaskConical, TriangleAlert } from "lucide-react";
import { useDemo } from "../context/DemoContext";
import { useAuth } from "../context/AuthContext";
import {
  PASSWORD_MIN_LENGTH,
  validatePasswordChange,
  type PasswordChangeErrors,
} from "../lib/password-change";

export function Settings() {
  const { profile, isDemo } = useDemo();
  const { configured, user, identity, changePassword } = useAuth();
  const [settingsTab, setSettingsTab] = useState<"account" | "integrations" | "preferences" | "appearance">("account");

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<PasswordChangeErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const liveAccount = configured && !isDemo && Boolean(user);

  async function submitPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setDone(false);
    setFormError(null);

    const validation = validatePasswordChange({ current, next, confirm });
    setErrors(validation.errors);
    if (!validation.ok) return;

    setSaving(true);
    const result = await changePassword(current, next);
    setSaving(false);

    if (!result.ok) {
      setFormError(result.error ?? "Não foi possível alterar a senha. Tente novamente.");
      return;
    }

    setDone(true);
    setCurrent("");
    setNext("");
    setConfirm("");
    setErrors({});
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Configurações</h1>
      </header>

      <div className="reference-tabs" role="tablist" aria-label="Seções de configuração">
        {([ ["account", "Sua conta"], ["integrations", "Integrações"], ["preferences", "Preferências"], ["appearance", "Aparência"] ] as const).map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={settingsTab === key} className={"reference-tab" + (settingsTab === key ? " is-active" : "")} onClick={() => setSettingsTab(key)}>{label}</button>
        ))}
      </div>

      {settingsTab === "account" && <section className="card p-4 mb-6" aria-label="Conta e workspace">
        <h2 className="text-lg font-semibold mb-4">Sua conta</h2>
        <dl className="reference-account-details">
          <div><dt>Email</dt><dd>{configured ? user?.email ?? "—" : "Conta demo local"}</dd></div>
          <div><dt>Workspace</dt><dd>{identity?.workspaceName ?? "Não carregado"}</dd></div>
          <div><dt>Função</dt><dd>{identity?.role ?? "—"}</dd></div>
        </dl>
      </section>}

      <section hidden={settingsTab !== "preferences"} className="card p-4 mb-6" aria-labelledby="profile-heading">
        <h2 id="profile-heading" className="text-lg font-semibold mb-4">Perfil</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Fuso horário</label>
            <select className="input" defaultValue={profile.timezone} disabled>
              <option value="America/Sao_Paulo">America/Sao_Paulo (Brasília)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
          <div>
            <label className="label">Idioma</label>
            <select className="input" defaultValue={profile.language} disabled>
              <option value="pt-BR">Português (Brasil)</option>
            </select>
          </div>
        </div>
      </section>

      <section hidden={settingsTab !== "integrations"} className="card p-4 mb-6" aria-labelledby="telegram-heading">
        <h2 id="telegram-heading" className="text-lg font-semibold mb-4">Integração Telegram</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Vínculo com bot do Telegram</p>
            <p className="text-sm text-[var(--color-text-muted)]">
              Site e Telegram devem compartilhar o mesmo backend, workspace e histórico.
            </p>
          </div>
          <span className="badge badge-neutral">Em preparação</span>
        </div>
        <div className="mt-4 p-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg">
            <p className="text-sm text-[var(--color-text-muted)]">O fluxo de vínculo e notificações ainda não está disponível. Nenhuma integração é marcada como conectada sem confirmação real do backend.</p>
        </div>
      </section>

      <section hidden={settingsTab !== "account"} className="card p-4 mb-6" aria-labelledby="demo-heading">
        <h2 id="demo-heading" className="text-lg font-semibold mb-4">Modo Demonstração</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Dados sintéticos para teste da interface</p>
            <p className="text-sm text-[var(--color-text-muted)]">
              Todas as telas usam dados fictícios claramente identificados. Não depende de credenciais externas.
            </p>
          </div>
          <span className={`badge ${isDemo ? "badge-demo" : "badge-neutral"}`}>
            {isDemo ? "Ativo" : "Inativo"}
          </span>
        </div>
      </section>

      {liveAccount && (
        <section hidden={settingsTab !== "account"} className="card p-4 mb-6" aria-labelledby="password-heading">
          <h2 id="password-heading" className="text-lg font-semibold mb-1">Senha</h2>
          <p className="text-sm text-[var(--color-text-muted)] mb-4">
            Conta: <span className="font-medium">{user?.email}</span>. A troca vale apenas para você e não
            exige sair do sistema.
          </p>

          <form onSubmit={submitPassword} className="grid gap-4 sm:max-w-md" noValidate>
            <div>
              <label className="label" htmlFor="password-current">Senha atual</label>
              <input
                id="password-current"
                className={`input ${errors.current ? "input-error" : ""}`}
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                disabled={saving}
              />
              {errors.current && (
                <p className="text-xs text-[var(--color-danger)] mt-1" role="alert">{errors.current}</p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="password-next">Nova senha</label>
              <input
                id="password-next"
                className={`input ${errors.next ? "input-error" : ""}`}
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                Mínimo de {PASSWORD_MIN_LENGTH} caracteres.
              </p>
              {errors.next && (
                <p className="text-xs text-[var(--color-danger)] mt-1" role="alert">{errors.next}</p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="password-confirm">Repita a nova senha</label>
              <input
                id="password-confirm"
                className={`input ${errors.confirm ? "input-error" : ""}`}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                disabled={saving}
              />
              {errors.confirm && (
                <p className="text-xs text-[var(--color-danger)] mt-1" role="alert">{errors.confirm}</p>
              )}
            </div>

            {formError && (
              <p className="text-sm text-[var(--color-danger)]" role="alert">{formError}</p>
            )}
            {done && (
              <p className="text-sm text-[var(--color-success)]" role="status">
                Senha alterada com sucesso.
              </p>
            )}

            <div>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? "Alterando..." : "Alterar senha"}
              </button>
            </div>
          </form>

          <p className="text-xs text-[var(--color-text-muted)] mt-4">
            A senha é alterada na sua própria sessão (Supabase Auth). Nenhuma chave de administrador é
            usada no navegador.
          </p>
        </section>
      )}

      <section hidden={settingsTab !== "account"} className="card p-4 mb-6" aria-labelledby="about-heading">
        <h2 id="about-heading" className="text-lg font-semibold mb-4">Sobre</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-[var(--color-text-muted)]">Nome oficial</dt>
            <dd className="font-medium">Tracker Bet Analystic</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--color-text-muted)]">Versão</dt>
            <dd className="font-medium">MVP privado</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--color-text-muted)]">Motor estatístico</dt>
            <dd className="font-medium">Poisson independente v0.1 (experimental)</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--color-text-muted)]">Provedor futebol (MVP)</dt>
            <dd className="font-medium">GoalDir/BSD (principal) / API-Football (fallback)</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--color-text-muted)]">Odds</dt>
            <dd className="font-medium">Manuais, 5 casas configuráveis</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--color-text-muted)]">Backend</dt>
            <dd className="font-medium">Supabase Edge Functions (TypeScript)</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--color-text-muted)]">Frontend</dt>
            <dd className="font-medium">React 18 + Vite + TypeScript estrito</dd>
          </div>
        </dl>
      </section>

      <section hidden={settingsTab !== "account"} className="card p-4 mb-6 border-[var(--color-danger)]" aria-labelledby="warnings-heading">
        <h2 id="warnings-heading" className="text-lg font-semibold text-[var(--color-danger)] mb-4"><TriangleAlert size={18} aria-hidden="true" /> Limitações Importantes</h2>
        <ul className="text-sm text-[var(--color-text-muted)] space-y-2">
          <li>• Resultados anteriores mostram o histórico e não garantem resultados futuros.</li>
          <li>• As comparações exibidas são experimentais e não são recomendações.</li>
          <li>• <strong>Dados ausentes permanecem null.</strong> Nunca são convertidos em zero.</li>
          <li>• As cotações das casas configuradas são registradas manualmente.</li>
          <li>• <strong>Sem cobrança, scraping ou apostas automáticas.</strong> Apenas análise sob demanda.</li>
          <li>• <strong>Segredos só no backend.</strong> Nenhuma chave VITE_* para service role, futebol, IA ou Telegram.</li>
        </ul>
      </section>

      {settingsTab === "preferences" && <section className="card p-4 mb-6">
        <h2 className="text-lg font-semibold mb-2">Competições e mercados favoritos</h2>
        <p className="text-sm text-[var(--color-text-muted)]">A preferência de ligas, times e mercados ainda não é persistida no workspace; nenhum filtro local será apresentado como configuração sincronizada.</p>
      </section>}

      {settingsTab === "appearance" && <section className="card p-4 mb-6" aria-labelledby="appearance-heading">
        <h2 id="appearance-heading" className="text-lg font-semibold mb-2">Aparência</h2>
        <p className="text-sm text-[var(--color-text-muted)]">Tema escuro ativo. A opção de alternar para tema claro ainda não está disponível.</p>
        <div className="reference-appearance-preview mt-4"><span aria-hidden="true">◐</span><div><strong>Escuro</strong><small>Preferência atual do produto</small></div><span className="badge badge-success">Ativo</span></div>
      </section>}

      {isDemo && (
        <div className="card p-4 bg-[var(--color-demo-light)] border border-[var(--color-demo)]">
          <p className="text-sm text-[var(--color-demo)]">
            <FlaskConical size={14} aria-hidden="true" /> <strong>Modo Demonstração ativo.</strong> Configurações são apenas ilustrativas.
            No modo real, estas preferências seriam persistidas no Supabase (tabela profiles).
          </p>
        </div>
      )}
    </div>
  );
}
